import { useState } from "react";
import { Checkbox, Icon, Label } from "semantic-ui-react";

import { DetailsCard } from "components";
import { useShowPriceInVat } from "hooks";
import { AppointmentReportDropdown, TotalPriceBar } from "modules/AppointmentDetails/components";
import { useInterventions } from "modules/AppointmentDetails/components/Interventions/hooks";
import "modules/AppointmentDetails/components/Interventions/Interventions.scss";
import { INTERVENTIONS_STORAGE_KEYS } from "modules/AppointmentDetails/components/Interventions/storageKeys";
import { useRealTimeOrderedChecks } from "modules/AppointmentDetails/hooks";
import { Intervention } from "modules/HoldingAppointmentDetails/components/Interventions/components";
import "modules/HoldingAppointmentDetails/components/Interventions/Interventions.scss";
import { isCarReadyOrQualityCheck } from "utils/appointmentUtils";
import { getPreference, setPreference } from "utils/common";

import type { SectionMenu } from "components";
import type { Appointment, DealerLocation, Intervention as InterventionModel } from "models";
import type { FormEvent } from "react";
import type { CheckboxProps } from "semantic-ui-react";

type InterventionsProps = {
  sectionMenu: SectionMenu;
  isExpanded: boolean;
  appointment: Appointment;
  appointmentLocation: DealerLocation;
};

const setInitialValueForCustomerOkFilter = () => getPreference(INTERVENTIONS_STORAGE_KEYS.InterventionsCustomerOkFilterPreference, false);

export const Interventions = ({ isExpanded, appointment, appointmentLocation, sectionMenu }: InterventionsProps) => {
  const [customerOkFilterChecked, setCustomerOkFilterChecked] = useState(setInitialValueForCustomerOkFilter);

  const { activeTab, interventionTotals, diagnoseOverviewEvent, convertedInterventions, localAndDmsInterventions, setActiveTab } = useInterventions(appointment);
  const { checks } = useRealTimeOrderedChecks(appointment.id);
  const showPriceInVat = useShowPriceInVat();

  const handleUpdateInterventionsFilter = (customerOkFilterChecked: boolean) => {
    setCustomerOkFilterChecked(customerOkFilterChecked);
    setPreference(INTERVENTIONS_STORAGE_KEYS.InterventionsCustomerOkFilterPreference, customerOkFilterChecked);
  };

  const handleUpdateCustomerOkFilterCheckbox = (evt: FormEvent<HTMLInputElement>, { checked = false }: CheckboxProps) => {
    evt.stopPropagation();
    handleUpdateInterventionsFilter(checked);
  };

  const customerOkFilterPreference = getPreference(INTERVENTIONS_STORAGE_KEYS.InterventionsCustomerOkFilterPreference, customerOkFilterChecked);
  const filterCustomerOkInterventions = (interventions?: InterventionModel[]) =>
    !customerOkFilterPreference || isCarReadyOrQualityCheck(appointment.appointment_status_identifier)
      ? interventions
      : interventions?.filter(intervention => intervention.customer_ok);

  const localAndDmsInterventionsToDisplay = filterCustomerOkInterventions(localAndDmsInterventions);
  const convertedInterventionsToDisplay = filterCustomerOkInterventions(convertedInterventions);
  const shouldDisableCustomerOkFilterCheckbox =
    (!localAndDmsInterventions.length && !convertedInterventions.length) ||
    (!localAndDmsInterventions.some(intervention => !intervention.customer_ok) && !convertedInterventions.some(intervention => !intervention.customer_ok));

  return (
    <DetailsCard
      sectionMenuId={sectionMenu.id}
      title={sectionMenu.count ? `${sectionMenu.title} (${sectionMenu.count})` : sectionMenu.title}
      icon={sectionMenu.icon}
      isExpanded={isExpanded}
      rightContent={
        <div className="interventions-details-card-right-content holding-readonly">
          {!isCarReadyOrQualityCheck(appointment.appointment_status_identifier) && (
            <Label basic className="intervention-customer-ok-toggle-contaier">
              <Icon className="check circle intervention-customer-ok-toggle-icon" color="green" />
              <Checkbox
                disabled={shouldDisableCustomerOkFilterCheckbox}
                className="intervention-customer-ok-toggle"
                toggle
                checked={customerOkFilterChecked}
                onChange={handleUpdateCustomerOkFilterCheckbox}
              />
            </Label>
          )}

          <AppointmentReportDropdown appointment={appointment} />
        </div>
      }
    >
      <div className="Interventions">
        {!!localAndDmsInterventionsToDisplay?.length && (
          <div className="Interventions-container">
            {localAndDmsInterventionsToDisplay.map((item, index) => (
              <Intervention
                appointment={appointment}
                appointmentLocation={appointmentLocation}
                checks={checks}
                key={item.id}
                index={index}
                intervention={item}
                activeIntervention={activeTab}
                diagnoseOverviewEvent={diagnoseOverviewEvent}
                onRequestCloseTabs={setActiveTab}
              />
            ))}
          </div>
        )}

        {!!convertedInterventionsToDisplay?.length && (
          <div className="Interventions-container">
            {convertedInterventionsToDisplay.map((item, index) => (
              <Intervention
                appointment={appointment}
                appointmentLocation={appointmentLocation}
                checks={checks}
                key={item.id}
                index={localAndDmsInterventions.length + index}
                intervention={item}
                activeIntervention={activeTab}
                diagnoseOverviewEvent={diagnoseOverviewEvent}
                onRequestCloseTabs={setActiveTab}
              />
            ))}
          </div>
        )}

        <TotalPriceBar appointment={appointment} interventionTotals={interventionTotals} showPriceInVat={showPriceInVat} />
      </div>
    </DetailsCard>
  );
};
